import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import Register from './pages/Register';
// import MyProject from './pages/myProject'; // Tas bort - använd MyProjects istället
// MyAssignedProjects ersatt av /workspace/* routes (LegacyRedirect hanterar gamla URLs)
import Settings from './pages/Settings';
import PrivateRoute from './routes/PrivateRoute';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PrislistaPanelProvider } from './contexts/PrislistaPanelContext';
import { DockadePanelerProvider } from './contexts/DockadePanelerContext';
import PrislistaPanel from './features/ia-prislista/components/PrislistaPanel';
import Toast from './components/common/Toast';
import GlobalModal from './components/common/GlobalModal';
import FirandeBanner from './components/common/FirandeBanner';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MyProjects from './pages/MyProjects';
import ChangePasswordPage from './pages/ChangePasswordPage';

// 🚀 OPTIMERING: Lägg till user activity tracking
import { useEffect } from 'react';

// Lazy load stora komponenter
const Produktval = lazy(() => import('./pages/Produktval'));
const Rumsbeskrivning = lazy(() => import('./pages/Rumsbeskrivning'));
const Tidplan = lazy(() => import('./pages/Tidplan'));
// Namnet skiljer sig från filen för att inte krocka med workspace-wrappern
const KundinfoSida = lazy(() => import('./pages/Kundinfo'));
const Underlag = lazy(() => import('./pages/Underlag'));
const Avtal = lazy(() => import('./pages/Avtal'));
const CustomerCertifications = lazy(() => import('./pages/CustomerCertifications'));
const BookingDashboard = lazy(() => import('./components/Bookings/BookingDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminFeedbackPage = lazy(() => import('./pages/AdminFeedbackPage'));
const ATACustomerPage = lazy(() => import('./pages/ATACustomerPage'));
const RitningarPage = lazy(() => import('./features/ritningar/components/RitningarPage'));

const ArbetsbeskrivningPage = lazy(() => import('./features/arbetsbeskrivning/pages/ArbetsbeskrivningPage'));
const AdminPricingPage = lazy(() => import('./features/arbetsbeskrivning/pages/AdminPricingPage'));
const AdminMaterialsPage = lazy(() => import('./features/arbetsbeskrivning/pages/AdminMaterialsPage'));
const EstimatPage = lazy(() => import('./pages/EstimatPage').then(m => ({ default: m.default ?? m })));
const EstimatView = lazy(() => import('./pages/EstimatView').then(m => ({ default: m.default ?? m })));
const Offert = lazy(() => import('./pages/Offert').then(m => ({ default: m.default ?? m })));
// ProcurementPage-importen togs bort: routen /procurement är utkommenterad
// nedan (se {/*<Route path="/procurement" ...*/}). Ska den återaktiveras
// måste lazy-importen läggas tillbaka.
const KalkylPage = lazy(() => import('./features/kalkyl/pages/KalkylPage'));
const ForstudiePresentation = lazy(() => import('./features/forstudie-presentation/ForstudiePresentation'));
const IAPresentation = lazy(() => import('./features/ia-presentation/IAPresentation'));
const MarketingDashboard = lazy(() => import('./features/marketing/pages/MarketingDashboard'));

// Workspace (nya fasbaserade navigeringen)
const WorkspaceLayout = lazy(() => import('./features/workspace/components/WorkspaceLayout'));
const OversiktWrapper = lazy(() => import('./features/workspace/sections/OversiktWrapper'));
const VeckaSection = lazy(() => import('./features/workspace/sections/VeckaSection'));
const MondaySection = lazy(() => import('./features/workspace/sections/MondaySection'));
const ProjektSection = lazy(() => import('./features/workspace/sections/ProjektSection'));
const UpphandlingSection = lazy(() => import('./features/workspace/sections/UpphandlingSection'));
const ProjekteringSection = lazy(() => import('./features/workspace/sections/ProjekteringSection'));
const ProduktionSection = lazy(() => import('./features/workspace/sections/ProduktionSection'));
const MinaPUSection = lazy(() => import('./features/workspace/sections/MinaPUSection'));
// UE kan inte återanvända byggarens vy — ModernPU läser /api/pu/my, som är
// spärrad till rollen byggare.
const MinaUeUnderlagSection = lazy(() => import('./features/workspace/sections/MinaUeUnderlagSection'));
const BehorigheterSection = lazy(() => import('./features/workspace/sections/BehorigheterSection'));
const KanbanWrapper = lazy(() => import('./features/workspace/sections/wrappers/KanbanWrapper'));
const BriefWrapper = lazy(() => import('./features/workspace/sections/wrappers/BriefWrapper'));
const AttGoraWrapper = lazy(() => import('./features/workspace/sections/wrappers/AttGoraWrapper'));
const ProduktvalWrapper = lazy(() => import('./features/workspace/sections/wrappers/ProduktvalWrapper'));
const RumsaWrapper = lazy(() => import('./features/workspace/sections/wrappers/RumsaWrapper'));
const TidplanWrapper = lazy(() => import('./features/workspace/sections/wrappers/TidplanWrapper'));
const KundinfoWrapper = lazy(() => import('./features/workspace/sections/wrappers/KundinfoWrapper'));
const UnderlagWrapper = lazy(() => import('./features/workspace/sections/wrappers/UnderlagWrapper'));
const AvtalWrapper = lazy(() => import('./features/workspace/sections/wrappers/AvtalWrapper'));
const ATAWrapper = lazy(() => import('./features/workspace/sections/wrappers/ATAWrapper'));
const BehorigheterWrapper = lazy(() => import('./features/workspace/sections/wrappers/BehorigheterWrapper'));
const MoetesWrapper = lazy(() => import('./features/workspace/sections/wrappers/MoetesWrapper'));
const MeetingResponsePage = lazy(() => import('./features/meetings/components/MeetingResponsePage'));
const DefaultSubTabRedirect = lazy(() => import('./features/workspace/components/DefaultSubTabRedirect'));
const LegacyRedirect = lazy(() => import('./features/workspace/components/LegacyRedirect'));
const DokumentationWrapper = lazy(() => import('./features/workspace/sections/wrappers/DokumentationWrapper'));
const Dokumentation = lazy(() => import('./pages/Dokumentation'));
const Erbjudanden = lazy(() => import('./pages/Erbjudanden'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
  </div>
);

function App() {
  // 🚀 OPTIMERING: Spåra användaraktivitet för smart notification polling
  useEffect(() => {
    const updateLastActivity = () => {
      localStorage.setItem('lastUserActivity', Date.now().toString());
    };

    // Lyssna på användarinteraktioner
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Initial aktivitet
    updateLastActivity();
    
    // Lägg till event listeners
    events.forEach(event => {
      document.addEventListener(event, updateLastActivity, { passive: true });
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateLastActivity);
      });
    };
  }, []);

  return (
    <AuthProvider>
      <SocketProvider>
        <NotificationProvider>
        {/* Prislistepanelen ligger utanför Routes så att den överlever
            navigering — IA ska kunna gå runt i portalen med den uppslagen. */}
        <DockadePanelerProvider>
        <PrislistaPanelProvider>
          <Toast />
          <GlobalModal />
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          {/* Firandet syns på alla sidor och överlever navigering. Ligger inuti
              Router men utanför Routes: den behöver veta vilken sida man står
              på för att kunna tiga på de utloggade sidorna — se
              UTLOGGADE_SIDOR i FirandeBanner. */}
          <FirandeBanner />
          <Routes>
            {/* Omdirigera från '/' till '/login' */}
            <Route path="/" element={<Navigate to="/login" />} />

            {/* Öppna rutter */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />

            {/* Publik mötesrespons-sida (ingen inloggning krävs) */}
            <Route
              path="/meeting-response/:meetingId/:token"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <MeetingResponsePage />
                </Suspense>
              }
            />

            {/* Skydda Dashboard-rutten */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            {/* Omdirigera /myproject till /myprojects */}
            <Route path="/myproject" element={<Navigate to="/myprojects" replace />} />
            {/* Bar /myproject/:projectId saknade route helt, så en länk utan
                underflik renderade en tom sida. Skickar vidare till produktval,
                kundens första flik. */}
            <Route path="/myproject/:projectId" element={<Navigate to="produktval" replace />} />
            <Route
              path="/assigned-projects"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <LegacyRedirect />
                  </Suspense>
                </PrivateRoute>
              }
            />
            {/*<Route 
              path="/procurement" 
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProcurementPage />
                  </Suspense>
                </PrivateRoute>
              } 
            />*/}
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/myprojects"
              element={
                <PrivateRoute>
                  <MyProjects />
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/produktval"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Produktval />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/rumsbeskrivning"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Rumsbeskrivning />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/timeline"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Tidplan />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/kundinfo"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <KundinfoSida />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/underlag"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Underlag />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/avtal"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Avtal />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/ritningar"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <RitningarPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/dokumentation"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Dokumentation />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/erbjudanden"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Erbjudanden />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/ata"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ATACustomerPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/myproject/:projectId/behorigheter"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <CustomerCertifications />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <BookingDashboard />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin-dashboard"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AdminDashboard />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/booking-dashboard"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <BookingDashboard />
                  </Suspense>
                </PrivateRoute>
              }
            />

            <Route
              path="/arbetsbeskrivning/:procurementId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ArbetsbeskrivningPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/arbetsbeskrivning-pricing"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AdminPricingPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/materials"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AdminMaterialsPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <AdminFeedbackPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/estimat/:leadId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <EstimatPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/estimat/view/:leadId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <EstimatView />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/estimat-view/:estimatId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <EstimatView />
                  </Suspense>
                </PrivateRoute>
              }
            />
            {/* Förstudie-presentation — full-screen, öppnas i nytt fönster för screenshare */}
            <Route
              path="/forstudie-presentation"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ForstudiePresentation />
                  </Suspense>
                </PrivateRoute>
              }
            />
            {/* IA-presentation — full-screen, ingen projektkontext. Rollspärren
                ligger i komponenten; PrivateRoute kollar bara inloggning. */}
            <Route
              path="/ia-presentation"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <IAPresentation />
                  </Suspense>
                </PrivateRoute>
              }
            />
            {/* Marknad — sub-tabs: content-plan / ideer / fotografering */}
            <Route path="/marketing" element={<Navigate to="/marketing/content-plan" replace />} />
            <Route
              path="/marketing/:tab"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <MarketingDashboard />
                  </Suspense>
                </PrivateRoute>
              }
            />
            {/* Offert routes - konsoliderad komponent för både konsult och kund */}
            <Route
              path="/offert-page/:offertId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Offert />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/offert/:offertId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Offert />
                  </Suspense>
                </PrivateRoute>
              }
            />
            
            {/* Kalkyl route */}
            <Route
              path="/kalkyl/:procurementId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <KalkylPage />
                  </Suspense>
                </PrivateRoute>
              }
            />

            {/* Workspace - ny fasbaserad navigering */}
            <Route
              path="/workspace"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <WorkspaceLayout />
                  </Suspense>
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="/workspace/oversikt" replace />} />
              <Route path="oversikt" element={<OversiktWrapper />} />
              <Route path="vecka" element={<VeckaSection />} />
              <Route path="monday" element={<MondaySection />} />
              <Route path="projekt" element={<ProjektSection />} />
              <Route path="upphandling" element={<UpphandlingSection />} />
              <Route path="projektering/:projectId" element={<ProjekteringSection />}>
                <Route index element={<DefaultSubTabRedirect section="projektering" />} />
                <Route path="kanban" element={<KanbanWrapper />} />
                <Route path="brief" element={<BriefWrapper />} />
                <Route path="attgora" element={<AttGoraWrapper />} />
                <Route path="produktval" element={<ProduktvalWrapper />} />
                <Route path="rumsa" element={<RumsaWrapper />} />
                <Route path="tidplan" element={<TidplanWrapper />} />
                <Route path="kundinfo" element={<KundinfoWrapper />} />
                <Route path="underlag" element={<UnderlagWrapper />} />
                <Route path="avtal" element={<AvtalWrapper />} />
                <Route path="moten" element={<MoetesWrapper />} />
                <Route path="ata" element={<ATAWrapper />} />
                <Route path="dokumentation" element={<DokumentationWrapper />} />
              </Route>
              {/* Bara byggaren använder Produktion. BK, IA och admin skickas
                  vidare till Projektering av ProduktionSection. */}
              <Route path="produktion/:projectId" element={<ProduktionSection />}>
                <Route index element={<DefaultSubTabRedirect section="produktion" />} />
                <Route path="ata" element={<ATAWrapper />} />
                <Route path="moten" element={<MoetesWrapper />} />
                {/* Protokoll har ingen egen vy längre — det visas på mötet */}
                <Route path="protokoll" element={<Navigate to="../moten" replace />} />
                <Route path="underlag" element={<UnderlagWrapper />} />
                <Route path="avtal" element={<AvtalWrapper />} />
                <Route path="dokumentation" element={<DokumentationWrapper />} />
                <Route path="behorigheter" element={<BehorigheterWrapper />} />
              </Route>
              <Route path="mina-pu" element={<MinaPUSection />} />
              <Route path="mina-underlag" element={<MinaUeUnderlagSection />} />
              <Route path="behorigheter" element={<BehorigheterSection />} />
            </Route>
          </Routes>
        </Router>
          <PrislistaPanel />
          <ToastContainer />
        </PrislistaPanelProvider>
        </DockadePanelerProvider>
        </NotificationProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;